import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

import { 
  ArrowRight, 
  CheckCircle, 
  Users, 
  TrendingUp, 
  Award,
  Play,
  Building2,
  Settings,
  BookOpen,
  Users2,
  Globe,
  Shield,
  Zap,
  Target,
  Rocket,
  X
} from 'lucide-react';
import AnimatedStats from '../components/AnimatedStats';
import AnimatedChart from '../components/AnimatedChart';
import ClientLogos from '../components/ClientLogos';
import InlineVideoPlayer from '../components/InlineVideoPlayer';

const Home: React.FC = () => {
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "CRMONCE - Microsoft Dynamics 365 & Power Platform Solutions",
    "description": "Leading Microsoft Dynamics 365 and Power Platform consulting company",
    "url": "https://crmonce.com",
    "mainEntity": {
      "@type": "Organization",
      "name": "CRMONCE",
      "url": "https://crmonce.com",
      "description": "Microsoft Dynamics 365 and Power Platform consulting services"
    }
  };
  const [showVideo, setShowVideo] = useState(false);
  const videoUrl = "https://www.youtube.com/embed/-aHlgpTOi1w?autoplay=1";
  const features = [
    {
      icon: Building2,
      title: "Dynamics 365 Implementation",
      description: "Complete CRM and ERP solutions tailored to your business needs",
      color: "from-blue-500 to-cyan-500",
      path: "/dynamics365-implementation"
    },
    {
      icon: Settings,
      title: "Power Platform Development",
      description: "Custom applications and automation to streamline operations",
      color: "from-purple-500 to-pink-500",
      path: "/power-platform-development"
    },
    {
      icon: BookOpen,
      title: "Professional Training",
      description: "Comprehensive training programs for your team",
      color: "from-green-500 to-emerald-500",
      path: "/professional-training"
    },
    {
      icon: Users2,
      title: "Consulting Services",
      description: "Expert guidance to optimize your business processes",
      color: "from-orange-500 to-red-500",
      path: "/consulting-services"
    }
  ];

  const stats = [
    {
      label: "Happy Clients",
      value: 33,
      suffix: "+",
      color: "text-blue-600",
      icon: <Users className="w-6 h-6" />
    },
    {
      label: "Projects Completed",
      value: 27,
      suffix: "+",
      color: "text-purple-600",
      icon: <Target className="w-6 h-6" />
    },
    {
      label: "Success Rate",
      value: 89,
      suffix: "%",
      color: "text-green-600",
      icon: <TrendingUp className="w-6 h-6" />
    },
    {
      label: "Years Experience",
      value: 5,
      suffix: "+",
      color: "text-orange-600",
      icon: <Award className="w-6 h-6" />
    }
  ];

  const clients = [
    {
      name: "SoftwareEdge",
      tagline: "THE PREVAILING SOLUTION",
      logo: "/images/clients/softwareedge-logo.png",
      description: "Leading software solutions provider"
    },
    {
      name: "XRM APPTECH",
      tagline: "INNOVATION DRIVEN",
      logo: "/images/clients/xrm-apptech-logo.png",
      description: "Cutting-edge technology solutions"
    },
         {
       name: "GMR IT SOLUTIONS",
       tagline: "CLOUD SOLUTIONS PROVIDER",
       logo: "/images/clients/gmr-it-logo.svg",
       description: "Comprehensive cloud infrastructure"
     }
  ];

  const benefits = [
    "24/7 Support & Maintenance",
    "Custom Development",
    "Data Migration",
    "User Training",
    "Performance Optimization",
    "Security Implementation"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
             <section className="hero-gradient particle-bg relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container-custom relative z-10">
                     <div className="flex flex-col lg:flex-row items-center min-h-[80vh] py-12 max-w-7xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2 text-center lg:text-left"
            >
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                                 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold gradient-text mb-6 leading-tight"
              >
                                 Transform Your Business with Dynamics 365
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                                 className="text-sm md:text-base lg:text-lg xl:text-xl text-black/90 mb-8 leading-relaxed max-w-4xl"
              >
                Expert implementation, customization, and training services to help your organization leverage the full power of Microsoft Dynamics 365 and Power Platform.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                                         className="btn-primary text-base px-6 py-3"
                  >
                    Get Started Today
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </motion.button>
                </Link>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                                     className="btn-outline text-base px-6 py-3 flex items-center justify-center"
                  onClick={() => setShowVideo(true)}
                >
                  <Play className="mr-2 w-5 h-5" />
                  Watch Demo
                </motion.button>
              </motion.div>
            </motion.div>

                         <motion.div 
               initial={{ opacity: 0, x: 50 }}
               animate={{ opacity: 1, x: 0 }}
               transition={{ delay: 0.4, duration: 0.8 }}
               className="lg:w-1/2 mt-12 lg:mt-0"
             >
               <div className="relative">
                 <div className="floating-animation">
                   <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-8 border border-white/30 shadow-2xl">
                                           {showVideo ? (
                        <InlineVideoPlayer
                          isOpen={showVideo}
                          onClose={() => setShowVideo(false)}
                          videoUrl={videoUrl}
                          title="CRMONCE Demo Video"
                        />
                      ) : (
                       <AnimatedChart 
                         data={[
                           { label: 'Sales', value: 65, color: '#3b82f6' },
                           { label: 'Marketing', value: 45, color: '#8b5cf6' },
                           { label: 'Service', value: 80, color: '#10b981' },
                           { label: 'Operations', value: 55, color: '#f59e0b' }
                         ]}
                         type="pie"
                       />
                     )}
                   </div>
                 </div>
               </div>
             </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="gradient-text">Trusted by Leading Companies</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've helped hundreds of organizations transform their business operations with our expertise.
            </p>
          </motion.div>

          <AnimatedStats stats={stats} />
        </div>
      </section>

      {/* Features Section */}
      <section className="section-gradient py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
             <span className="gradient-text">Our Services</span>
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'rgb(65 67 199 / 90%)' }}>
              Comprehensive solutions to help you succeed with Microsoft Dynamics 365 and Power Platform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Link key={feature.title} to={feature.path}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="feature-card cursor-pointer group hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6 shadow-2xl group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary-600 transition-colors">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  <div className="mt-4 flex items-center text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white/95 backdrop-blur-xl">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                <span className="gradient-text">Why Choose CRMONCE</span>?
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We combine deep technical expertise with business acumen to deliver solutions that drive real results for your organization.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="mt-8"
              >
                <Link to="/services">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-primary"
                  >
                    Explore Services
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="floating-animation">
                <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-3xl p-8 shadow-2xl">
                  <AnimatedChart 
                    data={[
                      { label: 'Implementation', value: 40, color: '#3b82f6' },
                      { label: 'Customization', value: 30, color: '#8b5cf6' },
                      { label: 'Training', value: 20, color: '#10b981' },
                      { label: 'Support', value: 10, color: '#f59e0b' }
                    ]}
                    type="area"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

             {/* Our Valuable Clients Section */}
       <section className="py-20 bg-white/95 backdrop-blur-xl">
         <div className="container-custom">
           <motion.div
             initial={{ opacity: 0, y: 50 }}
             whileInView={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.8 }}
             className="text-center mb-16"
           >
             <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
               <span className="gradient-text">Our Valuable Clients</span>
             </h2>
             <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto mb-8"></div>
           </motion.div>

           <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
             {clients.map((client, index) => (
               <motion.div
                 key={client.name}
                 initial={{ opacity: 0, y: 50 }}
                 whileInView={{ opacity: 1, y: 0 }}
                 transition={{ delay: index * 0.2, duration: 0.8 }}
                 className="flex flex-col h-full"
               >
                 <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col h-full">
                                       <div className="flex flex-col items-center text-center flex-grow">
                      <div className="w-20 h-16 lg:w-24 lg:h-16 mx-auto mb-4 flex items-center justify-center">
                        {client.logo ? (
                          <img 
                            src={client.logo} 
                            alt={`${client.name} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              // Fallback to SVG component if image fails to load
                              const target = e.currentTarget as HTMLElement;
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (target && fallback) {
                                target.style.display = 'none';
                                fallback.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-full h-full flex items-center justify-center ${client.logo ? 'hidden' : ''}`}
                          style={{ display: client.logo ? 'none' : 'flex' }}
                        >
                          <ClientLogos clientName={client.name} className="w-full h-full" />
                        </div>
                      </div>
                     <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">{client.name}</h3>
                     <p className="text-xs lg:text-sm text-primary-600 font-semibold mb-2">{client.tagline}</p>
                     <p className="text-xs lg:text-sm text-gray-600 leading-relaxed">{client.description}</p>
                   </div>
                 </div>
               </motion.div>
             ))}
           </div>
         </div>
       </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how CRMONCE can help you leverage the full power of Dynamics 365 and Power Platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-accent text-lg px-8 py-4"
                >
                  Get Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </motion.button>
              </Link>
              
              <Link to="/register">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-outline text-lg px-8 py-4"
                >
                  Start Your Journey
                </motion.button>
              </Link>
            </div>
          </motion.div>
                  </div>
        </section>
      </div>
  );
};

export default Home; 